import { useCallback, useEffect } from 'react';

import { useCommunicationDelay } from '../../store/apiHooks/useCommunicationDelay';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';

import { GameCommunicationConnectionObserver } from './GameCommunicationConnectionObserver';
import { GameCommunicationController } from './gameCommunicationController';
import {
  GameCommunicationBetProcessor,
  GameCommunicationBetValidationErrorProcessor,
  GameCommunicationBetsClosedProcessor,
  GameCommunicationBetsClosingSoonProcessor,
  GameCommunicationBetsOpenProcessor,
  GameCommunicationBetsProcessor,
  GameCommunicationCancelledProcessor,
  GameCommunicationDealerProcessor,
  GameCommunicationDuplicatedConnectionProcessor,
  GameCommunicationEnableSubmitProcessor,
  GameCommunicationGameInitProcessor,
  GameCommunicationGameProcessor,
  GameCommunicationMessageProcessor,
  GameCommunicationPromotionsProcessor,
  GameCommunicationRegulationProcessor,
  GameCommunicationStartDealingProcessor,
  GameCommunicationSubscribeProcessor,
  GameCommunicationSwitchProcessor,
  GameCommunicationTableCloseNotifyProcessor,
  GameCommunicationTableClosedProcessor,
  GameCommunicationTableProcessor,
  GameCommunicationTimerProcessor,
  GameCommunicationWinProcessor,
  GameCommunicationWinnersProcessor,
  GameCommunicationThirdPartyErrorProcessor,
  GameCommunicationCommandProcessor,
} from './processors';
import type { GameCommunicationControllerConnectorProps } from './types';

const gameCommunicationProcessors = [
  new GameCommunicationBetProcessor(),
  new GameCommunicationBetValidationErrorProcessor(),
  new GameCommunicationBetsClosedProcessor(),
  new GameCommunicationBetsClosingSoonProcessor(),
  new GameCommunicationBetsOpenProcessor(),
  new GameCommunicationBetsProcessor(),
  new GameCommunicationCancelledProcessor(),
  new GameCommunicationCommandProcessor(),
  new GameCommunicationDealerProcessor(),
  new GameCommunicationDuplicatedConnectionProcessor(),
  new GameCommunicationEnableSubmitProcessor(),
  new GameCommunicationGameInitProcessor(),
  new GameCommunicationGameProcessor(),
  new GameCommunicationMessageProcessor(),
  new GameCommunicationPromotionsProcessor(),
  new GameCommunicationRegulationProcessor(),
  new GameCommunicationStartDealingProcessor(),
  new GameCommunicationSubscribeProcessor(),
  new GameCommunicationSwitchProcessor(),
  new GameCommunicationTableCloseNotifyProcessor(),
  new GameCommunicationTableClosedProcessor(),
  new GameCommunicationTableProcessor(),
  new GameCommunicationThirdPartyErrorProcessor(),
  new GameCommunicationTimerProcessor(),
  new GameCommunicationWinProcessor(),
  new GameCommunicationWinnersProcessor(),
];

export const gameCommunicationController = new GameCommunicationController({
  processors: gameCommunicationProcessors,
  connectionObserver: new GameCommunicationConnectionObserver(),
});

export const GameCommunicationControllerConnector = ({
  gameStore,
  config,
}: GameCommunicationControllerConnectorProps): null => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const gameChannel = useGameStateSlice.gameChannel();
  const gameCommunicationDelay = useCommunicationDelay();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const isUserInfoReady = useGameStateSlice.userInfoReady();

  // Flag which prevents unnecessary connection in case of unavailable game table
  const shouldHaltCommunication = useCallback((): boolean => {
    const isTableClose = tableConfig?.tableClosed;

    return Boolean(isTableClose);
  }, [tableConfig]);

  useEffect(() => {
    if (config?.processors) {
      config.processors.forEach((processor) => gameCommunicationController.setProcessor(processor));
    }
    if (config?.senders) {
      config.senders.forEach((sender) => gameCommunicationController.setSender(sender));
    }
    if (config?.connectionObserver) {
      gameCommunicationController.setConnectionObserver(config.connectionObserver);
    }
  }, [config]);

  useEffect(() => {
    gameStore && gameCommunicationController.setStore(gameStore);
  }, [gameStore]);

  useEffect(() => {
    gameCommunicationController.stopStart(isGamePaused);
  }, [isGamePaused]);

  useEffect(() => {
    isGameAbandoned && gameCommunicationController.die();
  }, [isGameAbandoned]);

  useEffect(() => {
    gameChannel && gameCommunicationController.setInitialized();
  }, [gameChannel]);

  useEffect(() => {
    if (gameCommunicationDelay) {
      gameCommunicationController.setServerMessageDelay(gameCommunicationDelay);
    }
  }, [gameCommunicationDelay]);

  useEffect(() => {
    if (!shouldHaltCommunication() && tableConfig && JSESSIONID && tableId && isUserInfoReady) {
      gameCommunicationController.setCredentials(tableConfig.wsAddress, JSESSIONID, tableId);
      gameCommunicationController.start();
    }
  }, [shouldHaltCommunication, tableConfig, tableId, JSESSIONID, isUserInfoReady]);

  return null;
};
